import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import {
  DIVERGENCE_POPUP_STORAGE_KEY,
  isDivergencePopupActive,
  isDivergencePopupRoute,
} from "@shared/divergencePopup";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function hasSeenPopup() {
  try {
    const seenAt = Number(window.localStorage.getItem(DIVERGENCE_POPUP_STORAGE_KEY));
    return Number.isFinite(seenAt) && Date.now() - seenAt < 7 * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function markPopupSeen() {
  try {
    window.localStorage.setItem(DIVERGENCE_POPUP_STORAGE_KEY, String(Date.now()));
  } catch {
    // Storage can be disabled; the popup stays accessible without affecting page behavior.
  }
}

export function DivergenceLaunchPopup() {
  const [location] = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const eligible = isDivergencePopupRoute(location) && isDivergencePopupActive();

  useEffect(() => {
    if (!eligible || hasSeenPopup()) return;

    let opened = false;
    const open = () => {
      if (opened || hasSeenPopup()) return;
      opened = true;
      previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setIsOpen(true);
    };

    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    const timer = isMobile ? undefined : window.setTimeout(open, 8_000);
    const onScroll = () => {
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollableHeight > 0 && window.scrollY / scrollableHeight >= 0.4) open();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [eligible, location]);

  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;

      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!eligible && isOpen) close();
  }, [eligible, isOpen]);

  function close() {
    markPopupSeen();
    setIsOpen(false);
    window.setTimeout(() => previousFocusRef.current?.focus(), 0);
  }

  if (!isOpen || !eligible) return null;

  return (
    <div
      aria-hidden={false}
      className="fixed inset-0 z-[2147483000] flex items-center justify-center bg-slate-950/75 p-5 backdrop-blur-[2px]"
      onClick={(event) => {
        if (event.target === event.currentTarget) close();
      }}
      role="presentation"
    >
      <div
        aria-describedby="divergence-launch-description"
        aria-labelledby="divergence-launch-title"
        aria-modal="true"
        className="relative w-full max-w-[480px] rounded-2xl border border-[#243850] bg-[#0D1B2E] p-7 shadow-2xl sm:p-8"
        ref={dialogRef}
        role="dialog"
      >
        <button
          aria-label="Close announcement"
          className="absolute right-4 top-4 rounded-md p-2 text-slate-300 transition-colors hover:bg-slate-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#35E3E3]"
          onClick={close}
          ref={closeButtonRef}
          type="button"
        >
          <span aria-hidden="true" className="text-xl leading-none">×</span>
        </button>

        <img src="/manus-storage/prism-read-still_57b3edd7.png" alt="Prism reading a completed XAUUSD 1D market chart" className="mb-6 block max-h-56 w-full rounded-xl object-cover object-center" />
        <p className="text-[10px] font-black uppercase tracking-[0.28em] text-[#35E3E3]">PRISM · AI MARKET STRUCTURE ASSISTANT</p>
        <h2 id="divergence-launch-title" className="mt-3 max-w-sm pr-8 font-display text-2xl font-semibold tracking-tight text-[#EAF1F8] sm:text-[28px]">
          Your chart shows six layers. Prism reads all of them.
        </h2>
        <p id="divergence-launch-description" className="mt-4 text-[15px] leading-7 text-slate-300">
          Prism is an AI assistant that reads every Structura layer on your chart - and the ones we do not publish - and writes the read. Never buy, never sell.
        </p>

        <div className="mt-7">
          <Link
            className="inline-flex w-full items-center justify-center rounded-lg bg-[#35E3E3] px-5 py-3 text-sm font-bold text-[#0D1B2E] transition-transform duration-150 hover:bg-cyan-200 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#EAF1F8] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D1B2E]"
            href="/product#prism?src=popup"
            onClick={close}
          >
            See Prism read a chart →
          </Link>
          <button
            className="mt-4 w-full text-sm text-slate-400 underline-offset-4 transition-colors hover:text-slate-200 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#35E3E3]"
            onClick={close}
            type="button"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
